import { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { ArrowLeft, Paperclip, Plus, X } from 'react-feather';
import {
  PRIORIDADES,
  PRIORIDAD_KEYS,
  adjuntoDentroDeLimite,
  fileToBase64,
  tamanoLegible,
  ticketsService,
  useCrearTicket,
  useEtiquetas,
  useGrupos,
  useTemas,
  useTicketsUsuarios,
} from '../../features/tickets';

// Normaliza un nombre de etiqueta para comparar sin duplicar strings
// (ignora espacios sobrantes y mayúsculas).
const normEtiqueta = (s) => String(s || '').trim().toLowerCase();

const ESTADO_INICIAL = {
  solicitante_email: '',
  solicitante_nombre: '',
  asunto: '',
  cuerpo: '',
  grupo_id: '',
  tema_id: '',
  prioridad: 'normal',
  agente: '',
};

const TicketForm = ({ headquarter, onCancel, onCreated }) => {
  const { data: grupos = [] } = useGrupos(headquarter);
  const { data: temas = [] } = useTemas(headquarter);
  const { data: usuarios = [] } = useTicketsUsuarios(headquarter);
  const { data: etiquetasCatalogo = [] } = useEtiquetas(headquarter);
  const crearTicket = useCrearTicket(headquarter);

  const [form, setForm] = useState(ESTADO_INICIAL);
  const [error, setError] = useState('');
  const [archivos, setArchivos] = useState([]);
  const [avisoAdjunto, setAvisoAdjunto] = useState('');
  // Etiquetas elegidas para el nuevo ticket: { id?, nombre }.
  // Con id -> existente del catálogo (se enlaza); sin id -> se crea por nombre.
  const [etiquetas, setEtiquetas] = useState([]);
  const [etqValor, setEtqValor] = useState('');
  const [guardando, setGuardando] = useState(false);
  const fileRef = useRef(null);
  const etqRef = useRef(null);
  const etqListId = 'tickets-nuevo-etiquetas';

  const set = (campo) => (e) => setForm((prev) => ({ ...prev, [campo]: e.target.value }));

  // Sugerencias del datalist: catálogo menos las ya elegidas.
  const etiquetasDisponibles = etiquetasCatalogo.filter(
    (e) => !etiquetas.some((t) => normEtiqueta(t.nombre) === normEtiqueta(e.nombre)),
  );

  const addEtiqueta = () => {
    const nueva = etqValor.trim();
    if (!nueva) {
      setEtqValor('');
      return;
    }
    const yaElegida = etiquetas.some((t) => normEtiqueta(t.nombre) === normEtiqueta(nueva));
    if (!yaElegida) {
      // Si coincide (normalizado) con una etiqueta del catálogo la enlazamos
      // por id; si no, se marca como nueva. Así nunca duplicamos un string.
      const existente = etiquetasCatalogo.find(
        (e) => normEtiqueta(e.nombre) === normEtiqueta(nueva),
      );
      setEtiquetas((prev) => [
        ...prev,
        existente ? { id: existente.id, nombre: existente.nombre } : { nombre: nueva },
      ]);
    }
    setEtqValor('');
    if (etqRef.current) etqRef.current.focus();
  };

  // Añade una etiqueta existente del catálogo (clic en una sugerencia). Se
  // enlaza por id; no se duplica si ya está elegida.
  const elegirExistente = (e) =>
    setEtiquetas((prev) =>
      prev.some((t) => normEtiqueta(t.nombre) === normEtiqueta(e.nombre))
        ? prev
        : [...prev, { id: e.id, nombre: e.nombre }],
    );

  const quitarEtiqueta = (idx) => setEtiquetas((prev) => prev.filter((_, i) => i !== idx));

  const onEtqKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addEtiqueta();
    }
  };

  const onPickFiles = (e) => {
    const elegidos = Array.from(e.target.files || []);
    const validos = elegidos.filter(adjuntoDentroDeLimite);
    setAvisoAdjunto(
      validos.length < elegidos.length
        ? 'Algún archivo supera el límite de 3,7 MB y se descartó.'
        : '',
    );
    setArchivos((prev) => [...prev, ...validos]);
    if (fileRef.current) fileRef.current.value = '';
  };

  const quitarArchivo = (idx) => setArchivos((prev) => prev.filter((_, i) => i !== idx));

  const crear = async () => {
    if (!form.solicitante_email.trim() || !form.asunto.trim() || !form.cuerpo.trim()) {
      setError('Indica email del solicitante, asunto y descripción.');
      return;
    }
    if (!form.grupo_id) {
      setError('Selecciona una bandeja.');
      return;
    }
    setError('');

    // POST /tickets acepta los adjuntos inline: el backend los sube a Drive y
    // los inserta en adjuntos_tic ligados al primer comentario del ticket.
    // El contenido va en base64 SIN el prefijo "data:<mime>;base64,".
    let adjuntos;
    try {
      adjuntos = archivos.length
        ? await Promise.all(
            archivos.map(async (file) => ({
              nombre: file.name,
              tipo_mime: file.type,
              contenido_base64: await fileToBase64(file),
            })),
          )
        : undefined;
    } catch (e) {
      setError('No se pudieron leer los archivos adjuntos. Inténtalo de nuevo.');
      return;
    }

    setGuardando(true);
    try {
      const res = await crearTicket.mutateAsync({
        asunto: form.asunto.trim(),
        solicitante_email: form.solicitante_email.trim(),
        solicitante_nombre: form.solicitante_nombre.trim() || undefined,
        grupo_id: Number(form.grupo_id),
        tema_id: form.tema_id ? Number(form.tema_id) : undefined,
        prioridad: form.prioridad,
        canal: 'manual',
        agente: form.agente ? Number(form.agente) : undefined,
        cuerpo: form.cuerpo.trim(),
        adjuntos,
      });
      const ticketId = res && res.id;
      if (!ticketId) return;

      // Etiquetas: el backend no las acepta inline, así que las añadimos sobre
      // el ticket recién creado con POST /tickets/:id/etiquetas (etiqueta_id si
      // ya existe en el catálogo, nombre si es nueva). Best-effort: el ticket ya
      // existe, así que un fallo al etiquetar no bloquea la navegación (el toast
      // global de HttpService ya avisa) y evita que se reenvíe y se duplique.
      if (etiquetas.length) {
        try {
          await etiquetas.reduce(async (prev, etq) => {
            await prev;
            await ticketsService.agregarEtiqueta({
              headquarter,
              id: ticketId,
              etiquetaId: etq.id != null ? etq.id : undefined,
              nombre: etq.id != null ? undefined : etq.nombre,
            });
          }, Promise.resolve());
        } catch (e) {
          // se ignora: el ticket queda creado; las etiquetas se reintentan en
          // el detalle.
        }
      }

      onCreated(ticketId);
    } finally {
      setGuardando(false);
    }
  };

  return (
    <section className="tickets-panel tickets-form">
      <header className="tickets-detail__head">
        <button type="button" className="tickets-btn tickets-btn--ghost" onClick={onCancel}>
          <ArrowLeft size={16} aria-hidden="true" />
          Volver
        </button>
        <h1 className="tickets-detail__asunto">Nuevo ticket</h1>
      </header>

      <div className="tickets-form__grid">
        <label className="tickets-props__field">
          <span className="tickets-props__label">Email del solicitante</span>
          <input
            className="tickets-input"
            type="email"
            value={form.solicitante_email}
            onChange={set('solicitante_email')}
            placeholder="cliente@dominio.com"
          />
        </label>

        <label className="tickets-props__field">
          <span className="tickets-props__label">Nombre del solicitante</span>
          <input
            className="tickets-input"
            type="text"
            value={form.solicitante_nombre}
            onChange={set('solicitante_nombre')}
            placeholder="Opcional"
          />
        </label>

        <label className="tickets-props__field tickets-form__full">
          <span className="tickets-props__label">Asunto</span>
          <input
            className="tickets-input"
            type="text"
            value={form.asunto}
            onChange={set('asunto')}
            placeholder="Resumen del problema"
          />
        </label>

        <label className="tickets-props__field tickets-form__full">
          <span className="tickets-props__label">Descripción</span>
          <textarea
            className="tickets-textarea"
            value={form.cuerpo}
            onChange={set('cuerpo')}
            rows={4}
            placeholder="Detalle de la incidencia o solicitud"
          />
        </label>

        <label className="tickets-props__field">
          <span className="tickets-props__label">Bandeja</span>
          <select className="tickets-input" value={form.grupo_id} onChange={set('grupo_id')}>
            <option value="">Selecciona…</option>
            {grupos.map((g) => (
              <option key={g.id} value={g.id}>
                {g.nombre}
              </option>
            ))}
          </select>
        </label>

        <label className="tickets-props__field">
          <span className="tickets-props__label">Tema</span>
          <select className="tickets-input" value={form.tema_id} onChange={set('tema_id')}>
            <option value="">Sin tema</option>
            {temas.map((tm) => (
              <option key={tm.id} value={tm.id}>
                {tm.nombre}
              </option>
            ))}
          </select>
        </label>

        <label className="tickets-props__field">
          <span className="tickets-props__label">Prioridad</span>
          <select className="tickets-input" value={form.prioridad} onChange={set('prioridad')}>
            {PRIORIDAD_KEYS.map((p) => (
              <option key={p} value={p}>
                {PRIORIDADES[p].label}
              </option>
            ))}
          </select>
        </label>

        <label className="tickets-props__field">
          <span className="tickets-props__label">Asignar a</span>
          <select className="tickets-input" value={form.agente} onChange={set('agente')}>
            <option value="">Sin asignar</option>
            {usuarios.map((u) => (
              <option key={u.id} value={u.id}>
                {u.nombre}
              </option>
            ))}
          </select>
        </label>

        <div className="tickets-props__field tickets-form__full">
          <span className="tickets-props__label">Etiquetas</span>
          <div className="tickets-tags">
            {etiquetas.map((tag, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <span key={`${tag.nombre}-${i}`} className="tickets-tag">
                {tag.nombre}
                <button
                  type="button"
                  className="tickets-tag__remove"
                  aria-label={`Quitar etiqueta ${tag.nombre}`}
                  onClick={() => quitarEtiqueta(i)}
                >
                  <X size={11} aria-hidden="true" />
                </button>
              </span>
            ))}
            <input
              ref={etqRef}
              type="text"
              className="tickets-tag-input"
              list={etqListId}
              value={etqValor}
              maxLength={30}
              placeholder="Añadir o elegir etiqueta…"
              aria-label="Añadir etiqueta"
              onChange={(e) => setEtqValor(e.target.value)}
              onKeyDown={onEtqKeyDown}
            />
            <button
              type="button"
              className="tickets-tag tickets-tag--add"
              aria-label="Añadir etiqueta"
              onClick={addEtiqueta}
            >
              <Plus size={13} aria-hidden="true" />
            </button>
            <datalist id={etqListId}>
              {etiquetasDisponibles.map((e) => (
                <option key={e.id} value={e.nombre} />
              ))}
            </datalist>
          </div>
          {etiquetasDisponibles.length > 0 && (
            <div className="tickets-etiquetas-sugeridas">
              <span className="tickets-etiquetas-sugeridas__label">Existentes:</span>
              {etiquetasDisponibles.map((e) => (
                <button
                  key={e.id}
                  type="button"
                  className="tickets-tag tickets-tag--sugerida"
                  onClick={() => elegirExistente(e)}
                >
                  <Plus size={11} aria-hidden="true" />
                  {e.nombre}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="tickets-form__adjuntos">
        <button
          type="button"
          className="tickets-btn tickets-btn--ghost"
          onClick={() => fileRef.current && fileRef.current.click()}
        >
          <Paperclip size={16} aria-hidden="true" />
          Adjuntar archivos
        </button>
        <input
          ref={fileRef}
          type="file"
          multiple
          className="visually-hidden"
          onChange={onPickFiles}
          aria-hidden="true"
          tabIndex={-1}
        />
        {archivos.length > 0 && (
          <ul className="tickets-composer__files">
            {archivos.map((f, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <li key={`${f.name}-${i}`} className="tickets-adjunto">
                <Paperclip size={13} aria-hidden="true" />
                <span className="tickets-adjunto__nombre">{f.name}</span>
                <span className="tickets-adjunto__size">{tamanoLegible(f.size)}</span>
                <button
                  type="button"
                  className="tickets-tag__remove"
                  aria-label={`Quitar ${f.name}`}
                  onClick={() => quitarArchivo(i)}
                >
                  <X size={12} aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
        )}
        {avisoAdjunto && <p className="tickets-composer__error">{avisoAdjunto}</p>}
      </div>

      {error && <p className="tickets-composer__error">{error}</p>}

      <div className="tickets-form__actions">
        <button type="button" className="tickets-btn tickets-btn--secondary" onClick={onCancel}>
          Cancelar
        </button>
        <button
          type="button"
          className="tickets-btn tickets-btn--primary"
          onClick={crear}
          disabled={crearTicket.isPending || guardando}
        >
          {crearTicket.isPending || guardando ? 'Creando…' : 'Crear ticket'}
        </button>
      </div>
    </section>
  );
};

TicketForm.propTypes = {
  headquarter: PropTypes.string.isRequired,
  onCancel: PropTypes.func.isRequired,
  onCreated: PropTypes.func.isRequired,
};

export default TicketForm;
